import { RatingField } from "@overmap-ai/forms"
import type { ReactNode } from "react"
import type { NumberRange } from "src/typings"

import {
	NullableNumberEqualsFilterModifier,
	NullableNumberNotEqualsFilterModifier,
	NumberGreaterThanFilterModifier,
	NumberGreaterThanOrEqualsFilterModifier,
	NumberLessThanFilterModifier,
	NumberLessThanOrEqualsFilterModifier,
	NumberRangeInFilterModifier,
	NumberRangeNotInFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { RatingFieldFilterCell } from "./RatingFieldFilterCell"

type RatingFieldValue = number | null
type RatingFilterValue = number | NumberRange | null

export class RatingFieldFilterDef extends BaseFilterDef<RatingFieldValue, RatingFilterValue> {
	readonly id: string
	readonly defaultFilterValue: RatingFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: RatingField

	constructor(field: RatingField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableNumberEqualsFilterModifier,
			isValueValid: (_value: RatingFieldValue): _value is number | null => true,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number | null =>
				filterValue === null || typeof filterValue === "number",
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableNumberNotEqualsFilterModifier,
			isValueValid: (_value: RatingFieldValue): _value is number | null => true,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number | null =>
				filterValue === null || typeof filterValue === "number",
		}),
		lessThan: createFilterModifierConfig({
			modifier: NumberLessThanFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		lessThanOrEquals: createFilterModifierConfig({
			modifier: NumberLessThanOrEqualsFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThan: createFilterModifierConfig({
			modifier: NumberGreaterThanFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThanOrEquals: createFilterModifierConfig({
			modifier: NumberGreaterThanOrEqualsFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		inRange: createFilterModifierConfig({
			modifier: NumberRangeInFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
		notInRange: createFilterModifierConfig({
			modifier: NumberRangeNotInFilterModifier,
			isValueValid: (value: RatingFieldValue): value is number => value !== null,
			isFilterValueValid: (filterValue: RatingFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
	}

	render(props: FilterDefRenderProps<RatingFieldFilterDef>): ReactNode {
		return <RatingFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
